import {
  type ClientPerspective,
  createClient,
  QueryParams,
} from '@sanity/client'
import groq, { defineQuery } from 'groq'
import { SanityImageSource } from '@sanity/asset-utils'
import { createImageUrlBuilder } from '@sanity/image-url'
import { PortableTextBlock, PortableTextTextBlock } from '@sanity/types'
import type { PricingTemplateName } from '@/lib/cms/template-data'

type SanityQuery = {
  query: string
  params?: QueryParams
  tags?: string[]
  perspective?: ClientPerspective
}

export const SANITY_TOKEN = process.env.NEXT_PUBLIC_SANITY_READ_TOKEN
export const SANITY_PROJECT_ID = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID
export const SANITY_DATASET = process.env.NEXT_PUBLIC_SANITY_DATASET
export const IS_SANITY_PREVIEW_MODE =
  process.env.NEXT_PUBLIC_SANITY_PREVIEW_MODE === 'true'

export const SanityClient = createClient({
  projectId: SANITY_PROJECT_ID,
  dataset: SANITY_DATASET,
  useCdn: true,
  apiVersion: '2023-05-03',
})

const imgUrlBuilder = createImageUrlBuilder(SanityClient)

/**
 * Raw Sanity transport. Content reads should go through cmsFetch from
 * '@/lib/cms', which adds Mustache templating with pricing and social-proof data.
 */
export async function sanityFetch<QueryResponse>({
  query,
  params = {},
  tags = [],
  perspective,
}: SanityQuery): Promise<QueryResponse> {
  const activePerspective =
    perspective ?? (IS_SANITY_PREVIEW_MODE ? 'previewDrafts' : undefined)
  const requiresToken =
    activePerspective !== undefined && activePerspective !== 'published'

  if (requiresToken && !SANITY_TOKEN) {
    throw new Error(
      "The 'NEXT_PUBLIC_SANITY_READ_TOKEN' environment variable is required for Sanity drafts and releases",
    )
  }

  const isDevelopment = process.env.NODE_ENV === 'development'
  const cacheTags = tags.concat(['sanity'])
  const client = perspective
    ? SanityClient.withConfig({ apiVersion: '2025-02-19', useCdn: false })
    : SanityClient

  return client.fetch<QueryResponse>(query, params, {
    cache: isDevelopment || requiresToken ? undefined : 'force-cache',
    ...(activePerspective && {
      token: SANITY_TOKEN,
      perspective: activePerspective,
    }),
    next: {
      ...(requiresToken && { revalidate: 30 }),
      tags: cacheTags,
    },
  })
}

export async function sanityFetchPageMetadata(
  url: string,
): Promise<PageMetadata> {
  const query = groq`*[_type == "pageMetadata" && pageUrl == $url][0]{ 
    title, description
  }`

  return sanityFetch<PageMetadata>({ query, params: { url } })
}

export function urlFor(image: SanityImageSource) {
  return imgUrlBuilder.image(image)
}

// Sanity GROQ queries
// Get all posts
export const postsQuery = groq`*[_type == "post"] | order(publishedAt desc, _id desc) [$start..$end] {
    _id, _updatedAt, title, summary, slug, publishedAt, mainImage,
  }`

export const postCountQuery = groq`count(*[_type == "post"])`

// Get a single post by its slug
export const postQuery = groq`*[_type == "post" && slug.current == $slug][0]{ 
  _id, 
  _updatedAt, 
  title, 
  summary, 
  publishedAt, 
  mainImage, 
  body, 
  relevantProduct,
  medicallyReviewedBy[] { reviewDate, author-> { name, slug, titles } },
}`

export const conditionLandingPageQuery = groq`*[_type == "conditionLandingPage" && slug.current == $slug][0] {
  ...,
  metadata->,
  faqSection-> {
    questions[]->
  },
}`

export const allConditionsQuery = groq`*[_type == "conditionLandingPage"] {
  name,
  slug,
}`

export const landingPageV2Query = groq`*[_type == "landingPageV2"][0] {
  ...,
  faqs-> {
    questions[]->
  },
  extraContent->,
  features->,
  seoBlock,
}`

export const landingPageV3Query = defineQuery(`
  *[_id == "landingPageV3"][0] {
    _id,
    _type,
    metadata->{
      title,
      description
    },
    hero{
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image,
      imageAlt
    },
    services{
      eyebrow,
      title,
      items[]{
        _key,
        title,
        description,
        icon,
        theme,
        pricingKey,
        pricePrefix,
        href
      },
      footerLink{label, href}
    },
    treatments{
      eyebrow,
      title,
      description,
      items[]{
        _key,
        title,
        cta{label, href},
        image,
        imageAlt
      },
      footerLink{label, href}
    },
    howItWorks{
      eyebrow,
      title,
      steps[]{
        _key,
        label,
        title,
        description,
        icon,
        theme
      }
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    conditionsSection{
      eyebrow,
      title,
      conditionPages[]->{
        _id,
        name,
        slug
      },
      additionalConditions[]{
        _key,
        label,
        href
      },
      trailingText
    },
    faqSection{
      eyebrow,
      title,
      supportStatus,
      supportText,
      supportLink{label, href},
      faqList->{
        _id,
        questions[]->{
          _id,
          question,
          answer
        }
      }
    },
    closingCta{
      title,
      cta{label, href},
      image,
      imageAlt
    }
  }
`)

export const pricingLandingPageV2Query = defineQuery(`
  *[_id == "pricingLandingPageV2"][0] {
    _id,
    _type,
    metadata->{
      title,
      description
    },
    hero{
      eyebrow,
      title,
      description,
      mobileDescription,
      highlights
    },
    pricingGroups[]{
      _key,
      category,
      title,
      items[]{
        _key,
        title,
        description,
        pricingKey,
        priceSuffix,
        capabilities,
        cta{
          label,
          href
        }
      }
    },
    capabilityLabels{
      escript,
      delivery,
      deliverySoon
    },
    footnotes,
    medicationNotice{
      eyebrow,
      title,
      description
    },
    closingCta{
      title,
      description,
      cta{
        label,
        href
      }
    }
  }
`)

export const medicalCertificatesLandingPageV2Query = defineQuery(`
  *[_id == "medicalCertificatesLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image{desktop, mobile, alt}
    },
    pricing{
      eyebrow,
      title,
      items[]{
        _key,
        title,
        description,
        pricingKey,
        pricePrefix,
        icon,
        href
      }
    },
    process{
      eyebrow,
      title,
      image{desktop, mobile, alt},
      steps[]{_key, label, title, description, icon}
    },
    coverage{
      eyebrow,
      title,
      supported{label, items, description, cta{label, href}},
      unsupported{label, items, description, cta{label, href}}
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    reviews{
      eyebrow,
      title,
      items[]->{_id, author, city, state, content},
      footerLink{label, href}
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      list->{
        _id,
        questions[]->{_id, question, answer}
      }
    },
    closingCta{
      title,
      cta{label, href},
      image{desktop, mobile, alt}
    }
  }
`)

export const twoDayMedicalCertificatesLandingPageV2Query = defineQuery(`
  *[_id == "twoDayMedicalCertificatesLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image{desktop, mobile, alt}
    },
    choices{
      eyebrow,
      title,
      items[]{
        _key,
        kind,
        label,
        title,
        description,
        pricingKey,
        cta{label, href}
      },
      singleDayPrompt,
      singleDayLink{label, href}
    },
    process{
      eyebrow,
      title,
      image{desktop, mobile, alt},
      steps[]{_key, label, title, description, icon}
    },
    notes{
      eyebrow,
      title,
      items[]{_key, title, description, cta{label, href}}
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      list->{
        _id,
        questions[]->{_id, question, answer}
      }
    },
    closingCta{
      title,
      cta{label, href},
      image{desktop, mobile, alt}
    }
  }
`)

export const carerCertificatesLandingPageV2Query = defineQuery(`
  *[_id == "carerCertificatesLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image{desktop, mobile, alt}
    },
    pricing{
      eyebrow,
      title,
      items[]{
        _key,
        theme,
        label,
        title,
        description,
        pricingKey,
        cta{label, href}
      },
      longerCare{title, description, cta{label, href}},
      medicalPrompt,
      medicalLink{label, href}
    },
    process{
      eyebrow,
      title,
      image{desktop, mobile, alt},
      steps[]{_key, label, title, description, icon}
    },
    notes{
      eyebrow,
      title,
      description,
      items[]{_key, title, description, cta{label, href}}
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      list->{
        _id,
        questions[]->{_id, question, answer}
      }
    },
    closingCta{
      title,
      cta{label, href},
      image{desktop, mobile, alt}
    }
  }
`)

export const prescriptionsLandingPageV2Query = defineQuery(`
  *[_id == "prescriptionsLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image{desktop, mobile, alt}
    },
    pricing{
      eyebrow,
      title,
      items[]{
        _key,
        kind,
        title,
        description,
        benefits,
        pricingKey,
        cta{label, href}
      }
    },
    process{
      eyebrow,
      title,
      image{desktop, mobile, alt},
      steps[]{_key, label, title, description, icon}
    },
    escript{
      eyebrow,
      title,
      description,
      benefits,
      image{desktop, mobile, alt},
      message{label, text, link}
    },
    coverage{
      eyebrow,
      title,
      supported{label, items, description, cta{label, href}},
      unsupported{label, items, description, links[]{_key, label, href}}
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    safety{
      eyebrow,
      title,
      description,
      rating,
      registration,
      certificationLabel
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      list->{
        _id,
        questions[]->{_id, question, answer}
      }
    },
    closingCta{
      title,
      cta{label, href},
      image{desktop, mobile, alt}
    }
  }
`)

export const antibioticsScriptsLandingPageV2Query = defineQuery(`
  *[_id == "antibioticsScriptsLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label}
    },
    outcomes{
      eyebrow,
      title,
      items[]{_key, title, description, icon}
    },
    process{
      eyebrow,
      title,
      image,
      imageAlt,
      steps[]{_key, label, title, description, icon}
    },
    suitability{
      eyebrow,
      title,
      supported{label, items, description},
      unsupported{label, items, description}
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    standards{
      eyebrow,
      title,
      description,
      rating,
      registration,
      certificationLabel
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      questions[]{
        "_id": _key,
        _key,
        question,
        answer
      }
    },
    closingCta{
      title,
      cta{label, href},
      image,
      imageAlt
    }
  }
`)

export const bloodTestsLandingPageV2Query = defineQuery(`
  *[_id == "bloodTestsLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image{desktop, mobile, alt}
    },
    checks{eyebrow, title, items, note},
    process{
      eyebrow,
      title,
      image{desktop, mobile, alt},
      steps[]{_key, label, title, description, icon}
    },
    audience{
      eyebrow,
      title,
      items[]{_key, text, theme}
    },
    results{
      eyebrow,
      lines,
      details,
      cta{label, href}
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      list->{
        _id,
        questions[]->{_id, question, answer}
      }
    },
    closingCta{
      title,
      cta{label, href},
      image{desktop, mobile, alt}
    }
  }
`)

export const stdTestsLandingPageV2Query = defineQuery(`
  *[_id == "stdTestsLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image{desktop, mobile, alt}
    },
    checks{eyebrow, title, items, note},
    process{
      eyebrow,
      title,
      image{desktop, mobile, alt},
      steps[]{_key, label, title, description, icon}
    },
    audience{
      eyebrow,
      title,
      items[]{_key, text, theme}
    },
    results{
      eyebrow,
      lines,
      details,
      cta{label, href}
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    privacy{
      eyebrow,
      title,
      description,
      rating,
      registration,
      certificationLabel
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      list->{
        _id,
        questions[]->{_id, question, answer}
      }
    },
    closingCta{
      title,
      cta{label, href},
      image{desktop, mobile, alt}
    }
  }
`)

export const radiologyReferralsLandingPageV2Query = defineQuery(`
  *[_id == "radiologyReferralsLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image{desktop, mobile, alt}
    },
    scanTypes{eyebrow, title, items, note},
    process{
      eyebrow,
      title,
      image{desktop, mobile, alt},
      steps[]{_key, label, title, description, icon}
    },
    audience{
      eyebrow,
      title,
      suitable{label, items, cta{label, href}},
      unsuitable{label, items, note, cta{label, href}}
    },
    results{
      eyebrow,
      lines,
      details,
      cta{label, href}
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      list->{
        _id,
        questions[]->{_id, question, answer}
      }
    },
    closingCta{
      title,
      cta{label, href},
      image{desktop, mobile, alt}
    }
  }
`)

export const telehealthConsultationLandingPageV2Query = defineQuery(`
  *[_id == "telehealthConsultationLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image{desktop, mobile, alt}
    },
    services{
      eyebrow,
      title,
      items[]{_key, title, description, icon}
    },
    process{
      eyebrow,
      title,
      image{desktop, mobile, alt},
      steps[]{_key, label, title, description, icon}
    },
    coverage{
      eyebrow,
      title,
      suitable{label, items, note, cta{label, href}},
      unsuitable{label, items, note, cta{label, href}}
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    privacy{
      eyebrow,
      title,
      description,
      rating,
      registration,
      certificationLabel
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      list->{
        _id,
        questions[]->{_id, question, answer}
      }
    },
    closingCta{
      title,
      cta{label, href},
      image{desktop, mobile, alt}
    }
  }
`)

export const specialistReferralLandingPageV2Query = defineQuery(`
  *[_id == "specialistReferralLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image{desktop, mobile, alt}
    },
    pricing{
      eyebrow,
      title,
      options[]{
        _key,
        kind,
        title,
        description,
        details,
        price,
        cta{label, href}
      }
    },
    process{
      eyebrow,
      title,
      image{desktop, mobile, alt},
      steps[]{_key, label, title, description, icon}
    },
    specialists{eyebrow, title, items, note},
    reviews{
      eyebrow,
      title,
      items[]->{_id, author, city, state, content},
      cta{label, href}
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image,
      imageAlt
    },
    validity{
      eyebrow,
      title,
      description,
      rating,
      registration,
      certificationLabel
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      list->{
        _id,
        questions[]->{_id, question, answer}
      }
    },
    closingCta{
      title,
      cta{label, href},
      image{desktop, mobile, alt}
    }
  }
`)

export const treatmentLandingPageV2Query = defineQuery(`
  *[_id == $id][0] {
    _id,
    _type,
    internalName,
    theme,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label},
      image{desktop, mobile, alt}
    },
    pricing{
      eyebrow,
      title,
      layout,
      initialLabel,
      initialPrice,
      initialDescription,
      optionsLabel,
      optionsIntroduction,
      options[]{_key, title, description, price, priceNote, badge},
      image{desktop, mobile, alt},
      note
    },
    spotlight{
      layout,
      eyebrow,
      title,
      cards[]{
        _key,
        eyebrow,
        title,
        description,
        cta{label, href},
        image{desktop, mobile, alt}
      },
      items,
      caution,
      link{label, href},
      image{desktop, mobile, alt},
      notice{title, description}
    },
    process{
      eyebrow,
      title,
      steps[]{_key, label, title, description, icon}
    },
    guidance{
      eyebrow,
      title,
      items[]{_key, title, description}
    },
    editorial{
      eyebrow,
      titleLines,
      description,
      cta{label, href},
      chips,
      image{desktop, mobile, alt}
    },
    myths{
      eyebrow,
      title,
      items[]{_key, title, description}
    },
    privacy{
      eyebrow,
      title,
      description,
      rating,
      registration,
      certificationLabel,
      certificationImage
    },
    appPromo{
      eyebrow,
      title,
      description,
      benefits,
      image{desktop, mobile, alt}
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      list->{
        _id,
        questions[]->{_id, question, answer}
      }
    },
    closingCta{
      title,
      cta{label, href},
      image{desktop, mobile, alt}
    }
  }
`)

// Not sure why type cannot be used??
export const medicalCertificatesLandingPageQuery = groq`*[_id == "medicalCertificatesLandingPage"][0] {
  ...,
  faqs-> {
    questions[]->
  },
  extraContent->,
  features->,
  howToSection->,
  cardWithBackground->,
  textOnlySection->,
  threeColumnTextSection->,
  reviewSection {
    ...,
    reviews[]->
  },
  twoColumnGrid->,
  explainer,
}`

export const carerCertificatesLandingPageQuery = groq`*[_id == "carerCertificatesLandingPage"][0] {
  ...,
  faqs-> {
    questions[]->
  },
  extraContent->,
  features->,
  explainer,
}`

export const twoDayMedicalCertificatesLandingPageQuery = groq`*[_id == "twoDayMedicalCertificatesLandingPage"][0] {
  ...,
  faqs-> {
    questions[]->
  },
  extraContent->,
  features->,
  explainer,
}`

export const prescriptionsLandingPageQuery = groq`*[_id == "prescriptionsLandingPage"][0] {
  ...,
  faqs-> {
    questions[]->
  },
  features->,
  howToSection->,
  cardWithBackground->,
  textOnlySection->,
  threeColumnTextSection->,
  twoColumnGrid->,
}`

export const edPrescriptionsLandingPageQuery = groq`*[_id == "edPrescriptionsLandingPage"][0] {
  ...,
  howToSection->,
  cardWithBackground->,
  textOnlySection->,
  threeColumnTextSection->,
  faqs-> {
    questions[]->
  },
  
  "latestArticles": *[_type == "post" && "men-s-health" in prescriptionCategories[]] | order(publishedAt desc, _id desc) [0..2] {
    slug,
    title, 
    publishedAt, 
    mainImage, 
  }
}`

export const pePrescriptionsLandingPageQuery = groq`*[_id == "pePrescriptionsLandingPage"][0] {
  ...,
  howToSection->,
  cardWithBackground->,
  textOnlySection->,
  threeColumnTextSection->,
  faqs-> {
    questions[]->
  },
  
  "latestArticles": *[_type == "post" && "men-s-health" in prescriptionCategories[]] | order(publishedAt desc, _id desc) [0..2] {
    slug,
    title, 
    publishedAt, 
    mainImage, 
  }
}`

export const anxietyPrescriptionsLandingPageQuery = groq`*[_id == "anxietyPrescriptionsLandingPage"][0] {
  ...,
  howToSection->,
  cardWithBackground->,
  textOnlySection->,
  threeColumnTextSection->,
  faqs-> {
    questions[]->
  },
  
  "latestArticles": *[_type == "post" && "mental-health" in prescriptionCategories[]] | order(publishedAt desc, _id desc) [0..2] {
    slug,
    title, 
    publishedAt, 
    mainImage, 
  }
}`

export const anxietyPrescriptionsLandingPageV2Query = defineQuery(`
  *[_id == "anxietyPrescriptionsLandingPageV2"][0] {
    _id,
    _type,
    metadata->{title, description},
    hero{
      eyebrow,
      title,
      description,
      primaryCta{label, href},
      secondaryCta{label, href},
      stats[]{_key, value, label}
    },
    process{
      eyebrow,
      title,
      steps[]{_key, label, title, description, icon}
    },
    pathway{
      eyebrow,
      title,
      description,
      exclusions,
      followUp,
      cta{label, href}
    },
    escript{
      eyebrow,
      title,
      description,
      benefits
    },
    faqs{
      eyebrow,
      title,
      support{
        status,
        description,
        cta{label, href}
      },
      questions[]{
        "_id": _key,
        _key,
        question,
        answer
      }
    },
    closingCta{
      title,
      cta{label, href},
      image,
      imageAlt
    }
  }
`)

export const hairLossMalePrescriptionsLandingPageQuery = groq`*[_id == "hlmPrescriptionsLandingPage"][0] {
  ...,
  howToSection->,
  cardWithBackground->,
  textOnlySection->,
  threeColumnTextSection->,
  faqs-> {
    questions[]->
  },
  
  "latestArticles": *[_type == "post" && "hair-loss-men-" in prescriptionCategories[]] | order(publishedAt desc, _id desc) [0..2] {
    slug,
    title, 
    publishedAt, 
    mainImage, 
  }
}`

export const doctorsCertificateLandingPageQuery = groq`*[_id == "doctorsCertificateLandingPage"][0] {
  ...,
  extraContent->,
  howToSection->,
  cardWithBackground->,
  textOnlySection->,
  threeColumnTextSection->,
  faqs-> {
    questions[]->
  },
}`

export const specialistReferralLandingPageQuery = groq`*[_id == "specialistReferralLandingPage"][0] {
  ...,
  faqs-> {
    questions[]->
  },
  extraContent->,
  features->,
  explainer,
  howto,
  howToSection->,
  cardWithBackground->,
  reviewSection {
    ...,
    reviews[]->
  },
}`

export const localizedDoctorsLandingPageQuery = groq`*[_type == "localizedDoctorsLandingPage" && _id == $id][0] {
  ...,
  reviewSection {
    ...,
    reviews[]->
  },
}`

export const faqPageQuery = groq`*[_type == "faqPage"][0] {
  ...,
  sections[] {
    ...,
    list-> {
      questions[]->
    }
  }
}`

export const privacyPolicyQuery = groq`*[_type == "privacyPolicy"][0] {...}`

export const termsOfServiceQuery = groq`*[_type == "termsOfService"][0] {...}`

export const medicalCertificatesRequestCopyQuery = groq`*[_id == "medicalCertificateRequestCopy"][0] {
  title, body, linkDestination, linkDescription
}`

export const authorQuery = groq`*[_type == "author" && slug.current == $slug][0]{ 
    name, 
    slug, 
    demo, 
    titles, 
    photo, 
    bio, 
    socials,
    "reviewedArticles": *[_type == "post" && ^._id in medicallyReviewedBy[].author._ref] | order(publishedAt desc, _id desc) [0..3] {
      slug,
      title, 
      publishedAt, 
      mainImage, 
    }
  }`

export const authorSlugsQuery = groq`*[_type == "author" && demo == false] | order(slug.current asc) {
    slug
  }`

export const glossarySummariesQuery = groq`
  *[
    _type == "glossaryEntry" && slug.current >= $slug && 
    ($search == "" || lower(term) match lower($search)) &&
    ($firstChar == "" || (($firstChar == "#" && term match "^[^a-zA-Z].*") || string::startsWith(lower(term), lower($firstChar))))
  ] | order(lower(term) asc) [0..20] {
    term,
    slug,
    summary,
  }`

export const glossaryEntryQuery = groq`*[_type == "glossaryEntry" && slug.current == $slug][0]{
    ...,
  }`

export const glossarySlugsQuery = groq`*[_type == "glossaryEntry"] | order(slug.current asc) { slug }`

export const genericMcLandingPageQuery = groq`*[_id == $documentId][0] {
  ...,
  howto,
  howToSection->,
  cardWithBackground->,
  reviewSection {
    ...,
    reviews[]->
  },
  extraContent->,
  faqs-> {
    questions[]->
  },
}
`

export const telehealthConsultationPageQuery = groq`*[_id == "telehealthConsultationLandingPage"][0] {
  ...,
  extraContent->,
  howToSection->,
  faqs-> {
    questions[]->
  },
}
`

export const edTreatmentLandingPageQuery = groq`*[_id == "edTreatmentLandingPage"][0] {
  ...,
  metadata->,
  faqs-> {
    questions[]->
  },
}`

export const peTreatmentLandingPageQuery = groq`*[_id == "peTreatmentLandingPage"][0] {
  ...,
  metadata->,
  faqs-> {
    questions[]->
  },
}`

export const hairlossTreatmentLandingPageQuery = groq`*[_id == "hairlossTreatmentLandingPage"][0] {
  ...,
  metadata->,
  faqs-> {
    questions[]->
  },
}`

export const weightlossTreatmentLandingPageQuery = groq`*[_id == "weightlossTreatmentLandingPage"][0] {
  ...,
  metadata->,
  faqs-> {
    questions[]->
  },
}`

export const antibioticsScriptsLandingPageQuery = groq`*[_id == "antibioticsScriptsLandingPage"][0] {
  ...,
  metadata->,
  howToSection->,
  faqs-> {
    questions[]->
  },
}
`

export type TreatmentLandingPage = {
  metadata: {
    title: string
    description: string
  }
  faqs: FaqList
  information: Array<PortableTextTextBlock>
}

export type PeTreatmentLandingPage = {
  metadata: {
    title: string
    description: string
  }
  faqs: FaqList
  information: Array<PortableTextTextBlock>
}

export type ConditionPageConcise = {
  name: string
  slug: {
    current: string
  }
}

export type ConditionPage = {
  _id: string
  name: string

  metadata: {
    title: string
    description: string
  }

  heroSection: {
    title: string
    subtitle: string
  }

  howItWorksSection: {
    title: string
    steps: Array<{
      title: string
      description: string
    }>
  }

  freeTextSection1: {
    sections: Array<PortableTextTextBlock>
  }

  freeTextSection2: {
    sections: Array<PortableTextTextBlock>
  }

  faqSection: {
    _id: string
    questions: Array<FaqQuestion & { _id: string }>
  }

  ctaSection: {
    title: string
    description: string
  }

  ctaUrl: string
}

export type TelehealthConsultationLandingPage = {
  hero: HeroSection
  heroContent: Array<PortableTextBlock>
  howToSection: HowToSection
  extraContent: ExtraContent | null
  faqs: FaqList
}

export type GenericMcLandingPage = {
  hero: HeroSection
  howToSection: HowToSection
  cardWithBackground: CardWithBackground
  extraContent: ExtraContent | null
  reviewSection: UserReviewSection
  faqs: FaqList
}

export type SlugOnly = {
  slug: { current: string }
}

export type GlossarySummary = SlugOnly & {
  term: string
  summary: string
}

export type GlossaryEntry = GlossarySummary & {
  definition: Array<PortableTextBlock>
}

export type AuthorSlug = {
  slug: { current: string }
}

export type AuthorFull = {
  name: string
  slug: { current: string }
  demo: boolean
  titles: Array<string>
  photo: SanityImageSource
  bio: Array<PortableTextTextBlock>
  socials?: Array<AuthorSocial>
  reviewedArticles?: Array<ArticleConcise>
}

export type ArticleConcise = {
  slug: { current: string }
  title: string
  publishedAt: string
  mainImage: SanityImageSource
}

export type AuthorConcise = {
  slug: { current: string }
  name: string
  titles: Array<string>
}

export type AuthorSocial = {
  type:
    | 'TWITTER'
    | 'LINKEDIN'
    | 'FACEBOOK'
    | 'INSTAGRAM'
    | 'YOUTUBE'
    | 'WEBSITE'
    | 'TIKTOK'
  link: string
}

export type BlogId = {
  _id: string
  publishedAt: string
}

export type BlogPreview = {
  _updatedAt: string
  title: string
  summary: string
  mainImage: SanityImageSource
  slug: { current: string }
} & BlogId

export type BlogRelevantProduct =
  | 'MEDICAL_CERTIFICATE'
  | 'SPECIALIST_REFERRAL'
  | 'PATHOLOGY_REFERRAL'
  | 'RADIOLOGY_REFERRAL'
  | 'STD_TEST_REFERRAL'
  | 'GENERAL_CONSULTATION'
  | 'PRESCRIPTION'
  | 'ERECTILE_DYSFUNCTION'
  | 'PREMATURE_EJACULATION'
  | 'HAIR_LOSS'
  | 'WEIGHT_LOSS'
  | null

export type BlogPost = {
  _updatedAt: string
  relevantProduct: BlogRelevantProduct
  body: Array<PortableTextTextBlock>
  medicallyReviewedBy?: Array<MedicalReviewRecord>
} & BlogPreview

export type MedicalReviewRecord = {
  author: AuthorConcise
  reviewDate: string
}

export type FAQ = {
  question: string
  answer: string
}

export type FeatureSection = {
  subtitle: string
  title: string
  description: Array<PortableTextTextBlock>
  featureOneTitle: string
  featureOne: Array<PortableTextTextBlock>
  featureTwoTitle: string
  featureTwo: Array<PortableTextTextBlock>
  featureThreeTitle: string
  featureThree: Array<PortableTextTextBlock>
}

export type FaqList = {
  questions: Array<FaqQuestion>
}

export type ExtraContent = {
  identifier: string
  contentBlocks: Array<ExtraContentBlock>
}

export type SeoBlock = {
  paragraphs: Array<SeoBlockParagraph>
}

export type SeoBlockParagraph = {
  title: string
  content: Array<PortableTextTextBlock>
}

export type ExtraContentBlock = {
  identifier: string
  visibleText: Array<PortableTextTextBlock>
  readMoreText?: Array<PortableTextTextBlock>
}

export type ServiceLandingPage = {
  hero: HeroSection
  faqs: FaqList
  features: FeatureSection
  extraContent: ExtraContent | null
}

export type DoctorsCertificateLandingPage = {
  hero: HeroSection
  howToSection: HowToSection
  cardWithBackground: CardWithBackground
  textOnlySection: TextOnlySection
  threeColumnTextSection: ThreeColumnTextSection
  extraContent: ExtraContent | null
  faqs: FaqList
}

export type HomeLandingPage = ServiceLandingPage & {
  serviceList: ServiceList
  seoBlock: SeoBlock
}

type LandingPageLink = {
  label: string
  href: string
}

export type LandingPageV3 = {
  _id: string
  _type: 'landingPageV3'
  metadata: PageMetadata
  hero: {
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
    image: SanityImageSource
    imageAlt: string
  }
  services: {
    eyebrow: string
    title: string
    items: Array<{
      _key: string
      title: string
      description: string
      icon:
        | 'certificate'
        | 'prescription'
        | 'bloodTest'
        | 'telehealth'
        | 'specialistReferral'
        | 'radiology'
      theme: 'peach' | 'mint' | 'lavender' | 'blue' | 'slate'
      pricingKey: PricingTemplateName
      pricePrefix?: string
      href: string
    }>
    footerLink: LandingPageLink
  }
  treatments: {
    eyebrow: string
    title: string
    description: string
    items: Array<{
      _key: string
      title: string
      cta: LandingPageLink
      image: SanityImageSource
      imageAlt: string
    }>
    footerLink: LandingPageLink
  }
  howItWorks: {
    eyebrow: string
    title: string
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'questionnaire' | 'call' | 'document'
      theme: 'peach' | 'mint' | 'lavender' | 'blue' | 'slate'
    }>
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: SanityImageSource
    imageAlt: string
  }
  conditionsSection: {
    eyebrow: string
    title: string
    conditionPages: Array<{
      _id: string
      name: string
      slug: { current: string }
    }>
    additionalConditions: Array<{
      _key: string
      label: string
      href: string
    }>
    trailingText: string
  }
  faqSection: {
    eyebrow: string
    title: string
    supportStatus: string
    supportText: string
    supportLink: LandingPageLink
    faqList: {
      _id: string
      questions: Array<FaqQuestion & { _id: string }>
    }
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: SanityImageSource
    imageAlt: string
  }
}

type ResponsiveLandingImage = {
  desktop: SanityImageSource
  mobile: SanityImageSource
  alt: string
}

export type MedicalCertificatesLandingPageV2 = {
  _id: string
  _type: 'medicalCertificatesLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
    image: ResponsiveLandingImage
  }
  pricing: {
    eyebrow: string
    title: string
    items: Array<{
      _key: string
      title: string
      description: string
      pricingKey: PricingTemplateName
      pricePrefix?: string
      icon: 'singleDay' | 'twoDay' | 'carer' | 'longerLeave'
      href: string
    }>
  }
  process: {
    eyebrow: string
    title: string
    image: ResponsiveLandingImage
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'questionnaire' | 'doctorReview' | 'certificate'
    }>
  }
  coverage: {
    eyebrow: string
    title: string
    supported: {
      label: string
      items: string[]
      description: string
      cta: LandingPageLink
    }
    unsupported: {
      label: string
      items: string[]
      description: string
      cta: LandingPageLink
    }
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: SanityImageSource
    imageAlt: string
  }
  reviews: {
    eyebrow: string
    title: string
    items: Array<UserReview & { _id: string }>
    footerLink: LandingPageLink
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    list: {
      _id: string
      questions: Array<FaqQuestion & { _id: string }>
    }
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: ResponsiveLandingImage
  }
}

export type TwoDayMedicalCertificatesLandingPageV2 = {
  _id: string
  _type: 'twoDayMedicalCertificatesLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
    image: ResponsiveLandingImage
  }
  choices: {
    eyebrow: string
    title: string
    items: Array<{
      _key: string
      kind: 'medical' | 'carer'
      label: string
      title: string
      description: string
      pricingKey: PricingTemplateName
      cta: LandingPageLink
    }>
    singleDayPrompt: string
    singleDayLink: LandingPageLink
  }
  process: {
    eyebrow: string
    title: string
    image: ResponsiveLandingImage
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'questionnaire' | 'doctorCall' | 'certificateEmail'
    }>
  }
  notes: {
    eyebrow: string
    title: string
    items: Array<{
      _key: string
      title: string
      description: string
      cta?: LandingPageLink
    }>
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: SanityImageSource
    imageAlt: string
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    list: {
      _id: string
      questions: Array<FaqQuestion & { _id: string }>
    }
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: ResponsiveLandingImage
  }
}

export type CarerCertificatesLandingPageV2 = {
  _id: string
  _type: 'carerCertificatesLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
    image: ResponsiveLandingImage
  }
  pricing: {
    eyebrow: string
    title: string
    items: Array<{
      _key: string
      theme: 'lavender' | 'blue'
      label: string
      title: string
      description: string
      pricingKey: PricingTemplateName
      cta: LandingPageLink
    }>
    longerCare: {
      title: string
      description: string
      cta: LandingPageLink
    }
    medicalPrompt: string
    medicalLink: LandingPageLink
  }
  process: {
    eyebrow: string
    title: string
    image: ResponsiveLandingImage
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'questionnaire' | 'doctorCall' | 'certificateEmail'
    }>
  }
  notes: {
    eyebrow: string
    title: string
    description: string
    items: Array<{
      _key: string
      title: string
      description: string
      cta?: LandingPageLink
    }>
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: SanityImageSource
    imageAlt: string
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    list: {
      _id: string
      questions: Array<FaqQuestion & { _id: string }>
    }
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: ResponsiveLandingImage
  }
}

export type PrescriptionsLandingPageV2 = {
  _id: string
  _type: 'prescriptionsLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
    image: ResponsiveLandingImage
  }
  pricing: {
    eyebrow: string
    title: string
    items: Array<{
      _key: string
      kind: 'express' | 'consultation'
      title: string
      description: string
      benefits: string[]
      pricingKey: PricingTemplateName
      cta: LandingPageLink
    }>
  }
  process: {
    eyebrow: string
    title: string
    image: ResponsiveLandingImage
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'medicationSearch' | 'questionnaire' | 'doctorCall' | 'escript'
    }>
  }
  escript: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: ResponsiveLandingImage
    message: {
      label: string
      text: string
      link: string
    }
  }
  coverage: {
    eyebrow: string
    title: string
    supported: {
      label: string
      items: string[]
      description: string
      cta: LandingPageLink
    }
    unsupported: {
      label: string
      items: string[]
      description: string
      links: LandingPageLink[]
    }
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: SanityImageSource
    imageAlt: string
  }
  safety: {
    eyebrow: string
    title: string
    description: string
    rating: string
    registration: string
    certificationLabel: string
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    list: {
      _id: string
      questions: Array<FaqQuestion & { _id: string }>
    }
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: ResponsiveLandingImage
  }
}

export type AntibioticsScriptsLandingPageV2 = {
  _id: string
  _type: 'antibioticsScriptsLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
  }
  outcomes: {
    eyebrow: string
    title: string
    items: Array<{
      _key: string
      title: string
      description: string
      icon:
        | 'escript'
        | 'certificate'
        | 'referral'
        | 'test'
        | 'advice'
        | 'answer'
    }>
  }
  process: {
    eyebrow: string
    title: string
    image: SanityImageSource
    imageAlt: string
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'request' | 'call' | 'escript'
    }>
  }
  suitability: {
    eyebrow: string
    title: string
    supported: {
      label: string
      items: string[]
      description: string
    }
    unsupported: {
      label: string
      items: string[]
      description: string
    }
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: SanityImageSource
    imageAlt: string
  }
  standards: {
    eyebrow: string
    title: string
    description: string
    rating: string
    registration: string
    certificationLabel: string
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    questions: Array<FaqQuestion & { _id: string; _key: string }>
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: SanityImageSource
    imageAlt: string
  }
}

export type AnxietyPrescriptionsLandingPageV2 = {
  _id: string
  _type: 'anxietyPrescriptionsLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
  }
  process: {
    eyebrow: string
    title: string
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'questionnaire' | 'doctorCall' | 'escript'
    }>
  }
  pathway: {
    eyebrow: string
    title: string
    description: string
    exclusions: string[]
    followUp: string
    cta: LandingPageLink
  }
  escript: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    questions: Array<FaqQuestion & { _id: string; _key: string }>
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image?: SanityImageSource
    imageAlt: string
  }
}

export type BloodTestsLandingPageV2 = {
  _id: string
  _type: 'bloodTestsLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
    image: ResponsiveLandingImage
  }
  checks: {
    eyebrow: string
    title: string
    items: string[]
    note: string
  }
  process: {
    eyebrow: string
    title: string
    image: ResponsiveLandingImage
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'questionnaire' | 'doctorReview' | 'referral' | 'lab'
    }>
  }
  audience: {
    eyebrow: string
    title: string
    items: Array<{
      _key: string
      text: string
      theme: 'lavender' | 'mint' | 'peach' | 'blue' | 'grey'
    }>
  }
  results: {
    eyebrow: string
    lines: [string, string, string]
    details: [string, string, string]
    cta: LandingPageLink
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: SanityImageSource
    imageAlt: string
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    list: {
      _id: string
      questions: Array<FaqQuestion & { _id: string }>
    }
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: ResponsiveLandingImage
  }
}

export type StdTestsLandingPageV2 = Omit<BloodTestsLandingPageV2, '_type'> & {
  _type: 'stdTestsLandingPageV2'
  privacy: {
    eyebrow: string
    title: string
    description: string
    rating: string
    registration: string
    certificationLabel: string
  }
}

export type RadiologyReferralsLandingPageV2 = {
  _id: string
  _type: 'radiologyReferralsLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
    image: ResponsiveLandingImage
  }
  scanTypes: {
    eyebrow: string
    title: string
    items: string[]
    note: string
  }
  process: {
    eyebrow: string
    title: string
    image: ResponsiveLandingImage
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'questionnaire' | 'doctorReview' | 'referral' | 'imaging'
    }>
  }
  audience: {
    eyebrow: string
    title: string
    suitable: {
      label: string
      items: string[]
      cta: LandingPageLink
    }
    unsuitable: {
      label: string
      items: string[]
      note: string
      cta: LandingPageLink
    }
  }
  results: {
    eyebrow: string
    lines: [string, string, string]
    details: [string, string, string]
    cta: LandingPageLink
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: SanityImageSource
    imageAlt: string
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    list: {
      _id: string
      questions: Array<FaqQuestion & { _id: string }>
    }
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: ResponsiveLandingImage
  }
}

export type TelehealthConsultationLandingPageV2 = {
  _id: string
  _type: 'telehealthConsultationLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
    image: ResponsiveLandingImage
  }
  services: {
    eyebrow: string
    title: string
    items: Array<{
      _key: string
      title: string
      description: string
      icon:
        | 'prescription'
        | 'certificate'
        | 'specialistReferral'
        | 'tests'
        | 'telehealth'
        | 'answers'
    }>
  }
  process: {
    eyebrow: string
    title: string
    image: ResponsiveLandingImage
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'questionnaire' | 'phone' | 'issued'
    }>
  }
  coverage: {
    eyebrow: string
    title: string
    suitable: {
      label: string
      items: string[]
      note: string
      cta: LandingPageLink
    }
    unsuitable: {
      label: string
      items: string[]
      note: string
      cta: LandingPageLink
    }
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: SanityImageSource
    imageAlt: string
  }
  privacy: {
    eyebrow: string
    title: string
    description: string
    rating: string
    registration: string
    certificationLabel: string
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    list: {
      _id: string
      questions: Array<FaqQuestion & { _id: string }>
    }
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: ResponsiveLandingImage
  }
}

export type SpecialistReferralLandingPageV2 = {
  _id: string
  _type: 'specialistReferralLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{
      _key: string
      value: string
      label: string
    }>
    image: ResponsiveLandingImage
  }
  pricing: {
    eyebrow: string
    title: string
    options: Array<{
      _key: string
      kind: 'new' | 'renewal'
      title: string
      description: string
      details: string[]
      price: string
      cta: LandingPageLink
    }>
  }
  process: {
    eyebrow: string
    title: string
    image: ResponsiveLandingImage
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'questionnaire' | 'phone' | 'letter'
    }>
  }
  specialists: {
    eyebrow: string
    title: string
    items: string[]
    note: string
  }
  reviews: {
    eyebrow: string
    title: string
    items: Array<UserReview & { _id: string }>
    cta: LandingPageLink
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: SanityImageSource
    imageAlt: string
  }
  validity: {
    eyebrow: string
    title: string
    description: string
    rating: string
    registration: string
    certificationLabel: string
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    list: {
      _id: string
      questions: Array<FaqQuestion & { _id: string }>
    }
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: ResponsiveLandingImage
  }
}

export type TreatmentLandingPageV2 = {
  _id: string
  _type: 'treatmentLandingPageV2'
  internalName: string
  theme:
    | 'weightLoss'
    | 'erectileDysfunction'
    | 'prematureEjaculation'
    | 'hairLoss'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    primaryCta: LandingPageLink
    secondaryCta: LandingPageLink
    stats: Array<{ _key: string; value: string; label: string }>
    image: ResponsiveLandingImage
  }
  pricing: {
    eyebrow: string
    title: string
    layout: 'split' | 'image'
    initialLabel: string
    initialPrice: string
    initialDescription: string
    optionsLabel: string
    optionsIntroduction: string
    options: Array<{
      _key: string
      title: string
      description: string
      price: string
      priceNote: string
      badge?: string
    }>
    image?: ResponsiveLandingImage
    note?: string
  }
  spotlight?: {
    layout: 'cards' | 'audience'
    eyebrow: string
    title: string
    cards?: Array<{
      _key: string
      eyebrow: string
      title: string
      description: string
      cta: LandingPageLink
      image: ResponsiveLandingImage
    }>
    items: string[]
    caution?: string
    link?: LandingPageLink
    image: ResponsiveLandingImage
    notice: { title: string; description: string }
  }
  process: {
    eyebrow: string
    title: string
    steps: Array<{
      _key: string
      label: string
      title: string
      description: string
      icon: 'quiz' | 'phone' | 'treatment'
    }>
  }
  guidance: {
    eyebrow: string
    title: string
    items: Array<{ _key: string; title: string; description: string }>
  }
  editorial: {
    eyebrow: string
    titleLines: string[]
    description: string
    cta: LandingPageLink
    chips: string[]
    image: ResponsiveLandingImage
  }
  myths: {
    eyebrow: string
    title: string
    items: Array<{ _key: string; title: string; description: string }>
  }
  privacy: {
    eyebrow: string
    title: string
    description: string
    rating: string
    registration: string
    certificationLabel: string
    certificationImage: SanityImageSource
  }
  appPromo: {
    eyebrow: string
    title: string
    description: string
    benefits: string[]
    image: ResponsiveLandingImage
  }
  faqs: {
    eyebrow: string
    title: string
    support: {
      status: string
      description: string
      cta: LandingPageLink
    }
    list: {
      _id: string
      questions: Array<FaqQuestion & { _id: string }>
    }
  }
  closingCta: {
    title: string
    cta: LandingPageLink
    image: ResponsiveLandingImage
  }
}

export type ServiceList = {
  title: string
  medicalCertificatesSellingPoints: Array<TitleAndDescription>
  prescriptionsSellingPoints: Array<TitleAndDescription>
  specialistReferralSellingPoints: Array<TitleAndDescription>
  telehealthConsultationSellingPoints: Array<TitleAndDescription>
  edTreatmentSellingPoints: Array<TitleAndDescription>
  peTreatmentSellingPoints: Array<TitleAndDescription>
  hairlossTreatmentSellingPoints: Array<TitleAndDescription>
  weightlossTreatmentSellingPoints: Array<TitleAndDescription>
}

export type TitleAndDescription = {
  title: string
  description: string
}

export type MedicalCertificateProcessSection = {
  title: string
  description: string
  stepOneTitle: string
  stepOneDescription: string
  stepTwoTitle: string
  stepTwoDescription: string
  stepThreeTitle: string
  stepThreeDescription: string
}

export type PrescriptionsProcessSection = {
  title: string
  steps: Array<{ title: string; description: string }>
}

export type HowToSection = {
  _id: string
  title: Array<PortableTextTextBlock>
  description: string
  steps: Array<HowToSectionStep>
}

export type HowToSectionStep = {
  title: string
  body: string
  image: SanityImageSource
  imageAlt: string
}

export type CardWithBackground = {
  _id: string
  title: string
  subtitle: Array<PortableTextTextBlock>
  body: string
  background: SanityImageSource
}

export type TextOnlySection = {
  _id: string
  title: Array<PortableTextTextBlock>
  body: string
}

export type ThreeColumnTextSection = {
  _id: string
  columns: [TextColumn, TextColumn, TextColumn]
}

export type TextColumn = {
  title: string
  body: string
}

export type UserReviewSection = {
  title: string
  subtitle: string
  reviews: Array<UserReview>
}

export type UserReview = {
  state: string
  city: string
  author: string
  content: string
}

export type TwoColumnGridSection = {
  content: Array<TwoColumnGridCard>
}

export type TwoColumnGridCard = {
  colSpan: 'ONE' | 'TWO' | null
  title: string
  content: Array<PortableTextTextBlock>
  image: SanityImageSource
  imageAlt: string
}

export type LocalizedDoctorsLandingPage = {
  reviewSection: UserReviewSection
}

export type AntibioticsLandingPage = {
  hero: HeroSection
  notice: string
  howToSection: HowToSection
  seoBlock: SeoBlock
  faqs: FaqList
}

export type MedicalCertificateLandingPage = ServiceLandingPage & {
  howToSection: HowToSection
  textOnlySection: TextOnlySection
  cardWithBackground: CardWithBackground
  threeColumnTextSection: ThreeColumnTextSection
  twoColumnGrid: TwoColumnGridSection
  reviewSection: UserReviewSection
  processSection: MedicalCertificateProcessSection
  explainer: Array<ServiceExplainer>
}

export type SpecialistReferralLandingPage = ServiceLandingPage & {
  reviewSection: UserReviewSection
  howToSection: HowToSection
  cardWithBackground: CardWithBackground
  explainer: Array<ServiceExplainer>
  howto: SpecialistReferralHowtoSection
}

export type SpecialistReferralHowtoSection = {
  title: PortableTextBlock
  description: string
  steps: Array<{
    title: string
    body: Array<PortableTextBlock>
  }>
}

export type ServiceExplainer = {
  title: string
  body: Array<PortableTextTextBlock>
}

export type PrescriptionLandingPage = {
  hero: HeroSection
  faqs: FaqList
  howToSection: HowToSection
  cardWithBackground: CardWithBackground
  textOnlySection: TextOnlySection
  threeColumnTextSection: ThreeColumnTextSection
  twoColumnGrid: TwoColumnGridSection
  processSection: PrescriptionsProcessSection
}

export type TextContent = {
  title: string
  content: Array<PortableTextTextBlock>
}

export type PrescriptionConditionLandingPage = {
  hero: HeroSection
  textContent: TextContent
  howToSection: HowToSection
  cardWithBackground: CardWithBackground
  textOnlySection: TextOnlySection
  threeColumnTextSection: ThreeColumnTextSection
  faqs: FaqList
  latestArticles?: Array<ArticleConcise>
}

export type HeroSection = {
  title: string
  subTitle: string
  description: Array<PortableTextTextBlock>
}

export type FaqPage = {
  sections: Array<FaqSection>
}

export type FaqSection = {
  title: string
  list: FaqList
}

export type FaqQuestion = {
  question: string
  answer: Array<PortableTextTextBlock>
}

export type LegalDocument = {
  body: PortableTextBlock
  lastUpdatedAt: string
}

export type PageMetadata = {
  title: string
  description: string
}

export type PricingLandingPageV2 = {
  _id: string
  _type: 'pricingLandingPageV2'
  metadata: PageMetadata
  hero: {
    eyebrow: string
    title: string
    description: string
    mobileDescription?: string
    highlights: string[]
  }
  pricingGroups: Array<{
    _key: string
    category:
      | 'certificates'
      | 'prescriptions'
      | 'consultations'
      | 'referrals'
      | 'treatments'
    title: string
    items: Array<{
      _key: string
      title: string
      description: string
      pricingKey: PricingTemplateName
      // Shown after the price, e.g. "per month" for an ongoing plan fee.
      priceSuffix?: string | null
      capabilities?: Array<'escript' | 'delivery' | 'deliverySoon'>
      cta: {
        label: string
        href: string
      }
    }>
  }>
  capabilityLabels: Record<'escript' | 'delivery' | 'deliverySoon', string>
  footnotes: string[]
  medicationNotice: {
    eyebrow: string
    title: string
    description: string
  }
  closingCta: {
    title: string
    description: string
    cta: {
      label: string
      href: string
    }
  }
}

export type MedicalCertificateRequestCopy = {
  title: string
  body: string
  linkDestination: string
  linkDescription: string
}

export const bloodTestsOnlineLandingPageQuery = groq`*[_id == "bloodTestsLandingPage"][0] {
  ...,
  faqs-> {
    questions[]->
  },
}`

export const radiologyReferralsLandingPageQuery = groq`*[_id == "radiologyReferralsLandingPage"][0] {
  ...,
  faqs-> {
    questions[]->
  },
}`

export type BloodTestsLandingPage = {
  hero: {
    eyebrow: string
    title: string
    description: string
    subDescription: string
    cta: string
  }

  howItWorksSection: {
    eyebrow: string
    title: string
    steps: Array<{
      title: string
      description: Array<PortableTextTextBlock>
    }>
  }

  whatYouGetSection: {
    eyebrow: string
    title: string
    description: Array<PortableTextTextBlock>
    commonChecksPanel: {
      eyebrow: string
      title: string
      panels: Array<string>
    }
  }

  pricingSection: {
    points: Array<{
      title: string
      description: string
    }>
  }

  whoThisIsForSection: {
    eyebrow: string
    title: string
    description: string
    subDescription: string
    reasons: Array<string>
  }

  whyUsSection: {
    eyebrow: string
    title: string
    points: Array<{
      title: string
      description: string
    }>
  }

  faqs: FaqList

  seoBlock: {
    eyebrow: string
    title: string
    description: Array<PortableTextTextBlock>
  }
}

export type RadiologyReferralsLandingPage = {
  hero: {
    eyebrow: string
    title: string
    description: string
    subDescription: string
    cta: string
    secondaryCta: string
  }

  howItWorksSection: {
    eyebrow: string
    title: string
    steps: Array<{
      title: string
      description: Array<PortableTextTextBlock>
    }>
  }

  whatYouGetSection: {
    eyebrow: string
    title: string
    description: Array<PortableTextTextBlock>
    scanTypesPanel: {
      eyebrow: string
      title: string
      scanTypes: Array<string>
      description: Array<PortableTextTextBlock>
    }
  }

  pricingSection: {
    eyebrow: string
    price: string
    description: string
    points: Array<{
      title: string
      description: string
    }>
  }

  whoThisIsForSection: {
    eyebrow: string
    title: string
    description: string
    subDescription: string
    reasons: Array<string>
  }

  whyUsSection: {
    eyebrow: string
    title: string
    points: Array<string>
  }

  faqs: FaqList

  seoBlock: {
    eyebrow: string
    title: string
    description: Array<PortableTextTextBlock>
  }

  finalCtaSection: {
    title: string
    description: string
    subDescription: string
    cta: string
  }
}
